import { _findOrIndex, setOldOrsInAnd } from '../../../helpers/handle_manage_old_scenes';
import { _deepClone, _deepCopyArr } from '../../../helpers/utilities';

/**
 * How many consecutive `and` slots the edited row occupies (SBRC scroll steps + channel is two elements).
 */
const editedAndSlotSpan = (andArr: unknown[], subInd: number): number => {
	const el = andArr[subInd] as { rr?: string; r?: string } | undefined;
	if (!el) {
		return 1;
	}
	const ref = el.rr ?? el.r;
	if (typeof ref === 'string' && ref.includes(':rotie1s')) {
		const next = andArr[subInd + 1] as { rr?: string; r?: string } | undefined;
		const nextRef = next?.rr ?? next?.r;
		if (typeof nextRef === 'string' && nextRef.endsWith(':channel')) {
			return 2;
		}
	}
	return 1;
};

const mergeEditedIntoAndGroup = (
	existingAnd: unknown[] | undefined,
	subInd: number,
	replacementFragment: unknown[],
): unknown[] => {
	if (!Array.isArray(existingAnd) || subInd < 0 || subInd >= existingAnd.length) {
		return [...replacementFragment];
	}
	const span = editedAndSlotSpan(existingAnd, subInd);
	return [
		...existingAnd.slice(0, subInd),
		...replacementFragment,
		...existingAnd.slice(subInd + span),
	];
};

/** ------ CONDITIONS PART ------ */
/** Take care where to insert edited condition into
 * wizard scene object (parsed condition to scene service format) */
export const edit_condition = (scene, edited_cond_info, cond) => {
	let { main_ind, sub_ind } = edited_cond_info;

	let _scene: any = _deepClone(scene);

	if (main_ind !== undefined && sub_ind !== undefined && Array.isArray(cond.and)) {
		if (_scene?.if?.and) {
			let orIndex = _findOrIndex(scene);
			const prevAnd = _scene.if.and[orIndex].or[main_ind].and;
			_scene.if.and[orIndex].or[main_ind].and = mergeEditedIntoAndGroup(
				prevAnd,
				sub_ind,
				cond.and,
			);
		} else if (scene?.if?.or) {
			const prevAnd = _scene.if.or[main_ind].and;
			_scene.if.or[main_ind].and = mergeEditedIntoAndGroup(prevAnd, sub_ind, cond.and);
		}

		return _scene;
	}
	return _scene;
};

/** Take care where to insert new condition into
 * wizard scene object (parsed condition to scene service format) */
export const add_condition = (_scene, newCondition, operatorName = undefined, andInd = undefined) => {
	let scene: any = _deepClone(_scene);
	/**
	 * Because of very broken scenes
	 * 1. first check what there is in the IF
	 *  1.1 there is a case where the condition is directly set in IF, so format IF in OR way
	 *  - this would be only on edit scene
	 */
	if (!scene?.if?.or && !scene?.if?.and) {
		//clear all other wrong saved elements
		scene.if = {
			or: [],
		};
	}

	// must check what is set up and if there is no or but has active time, set or
	if (scene?.if?.and) {
		let orIndex = _findOrIndex(scene);

		if (!operatorName || operatorName === 'or' || operatorName === '') {
			scene.if.and[orIndex].or.push(newCondition);
		} else {
			//AND button is pressed
			scene.if.and[orIndex].or[andInd].and.push(newCondition.and[0]);
		}
	} else {
		// when we have if.or

		if (!operatorName || operatorName === 'or' || operatorName === '') {
			scene.if.or.push(newCondition);
		} else {
			//AND button is pressed

			scene.if.or[andInd].and.push(newCondition.and[0]);
		}
	}
	return scene;
};

/** Take care how to edit scene active time
 * (parsed condition to scene service format)
 * - if exists - edit it
 * - else - put it into the scene wizard object */
// export const edit_scene_active_time_old = (scene_object, newElement) => {
// 	let _scene: any = _deepClone(scene_object);

// 	if (_scene?.if?.or) {
// 		// this delete if.or and set if.and = [{or: old}]
// 		_scene = setOldOrsInAnd(_scene);

// 		_scene.if.and.push(newElement);
// 	} else {
// 		let _indailt_ind = _scene.if.and.findIndex(
// 			el => el.indailyint !== undefined
// 		);
// 		//update other element
// 		if (_indailt_ind > -1) {
// 			_scene.if.and[_indailt_ind] = { ...newElement };
// 		} else {
// 			_scene.if.and.push(newElement);
// 		}
// 	}
// 	return _scene;
// };

export const edit_scene_active_time = (
	scene_object,
	new_interval,
	type: 'add' | 'edit',
	index: number = 0,
) => {
	let _scene: any = _deepClone(scene_object);
	/**
	 * if the scene contains OR, this means the user never changed before the default active time - whole week M-S 00:00 - 23:59
	 * so now we need to update the scene object
	 **/
	if (_scene?.if?.or) {
		// this delete if.or and set if.and = [{or: []},{or: old}]
		_scene = setOldOrsInAnd(_scene);

		// the first "or" here is always about active time and we put the active time at the first position
		_scene.if.and[0].or.push({ ...new_interval });
	} else {
		let _indailt_ind = _scene.if.and.findIndex((el) => el.indailyint !== undefined);
		//update other element
		/** if the user edits the default interval, we need to change whole structure here */
		/** Let try to use only 2 formats when add and edit scene */
		if (_indailt_ind > -1) {
			_scene.if.and[_indailt_ind] = {
				or: [{ ...new_interval }],
			};
		} else {
			//we have at least one interval, so we need to find correct OR index and push or edit the interval
			let _or_ind_with_indailyints = _scene.if.and.findIndex((el) =>
				el?.or?.find((el) => el.indailyint),
			);
			if (_or_ind_with_indailyints > -1) {
				if (type === 'add') {
					_scene.if.and[_or_ind_with_indailyints].or.push({ ...new_interval });
				} else {
					_scene.if.and[_or_ind_with_indailyints].or[index] = {
						...new_interval,
					};
				}
			} else {
				//	console.log("CAN NOT FIND ANY DATA ABOUT ACTIVE TIME")
			}
		}
	}
	return _scene;
};

export const delete_active_time_by_ind = (scene_object, ind) => {
	let _scene: any = _deepClone(scene_object);
	let active_time_or = _scene?.if?.and?.find((el) => el?.or?.every((e) => e.indailyint))?.or || [];
	//if the active time array has only one element don't delete it
	if (active_time_or.length > 1) {
		active_time_or = active_time_or.filter((e, index) => index !== ind);
	}

	let find_active_time_or_index = _scene?.if?.and?.findIndex((el) =>
		el?.or?.every((e) => e.indailyint),
	);

	// console.log(find_active_time_or_index)
	if (find_active_time_or_index > -1) {
		_scene.if.and[find_active_time_or_index].or = [...active_time_or];
	}

	return _scene;
};

export const delete_condition_from_whens = (_whens, main_ind, sub_ind) => {
	//1. copy the array of objects
	//2. delete the selected nested AND from OR[parentArrayIndex].and -
	// in other words delete sub_ind from main_ind in all ORs
	// actually we find out current single and in parent and // something like this and = [and1, and2]
	// and return and = [and1] (if index we want to delete is equal to 1)
	//3. if parent AND (main_ind) contains only one 'and' and we delete it (step 2)
	// else we update the main_ind AND with the new array of nested AND (without deleted sub_ind)
	//4. return the new whens array

	//step 1
	let whens = _deepCopyArr(_whens);

	// step 2
	let withoutSelectedAnd = whens[main_ind].and.filter((el, ind) => ind != sub_ind);

	// step 3,
	//we need to check lenght because of this case
	// parent AND (whens = [and.lenght=2, and.length=0])
	// will return whens = [and.length=2]

	if (withoutSelectedAnd.length === 0) {
		//find the main_ind  element and delete it if there is no more elements (it is an OR element in all ORs)
		let up = whens.filter((single_or_el, ind) => ind != main_ind);
		whens = [...up];
	} else {
		//update or with new array without clicked AND index
		whens[main_ind].and = [...withoutSelectedAnd];
	}

	//step 4
	return whens;
};

export const update_scene_whit_new_whens = (scene, global) => {
	let _scene: any = _deepClone(scene);

	/** GET the new whens array and find the position or global OR condition
	 *  **depends or where and does the indailyint is changes by the user
	 */
	if (_scene?.if?.or) {
		_scene.if.or = [...global] || [];
	} else {
		let orInd = _findOrIndex(_scene);
		_scene.if.and[orInd].or = [...global] || [];
	}

	return _scene;
};

/** ---- END CONDITIONS ----- */

/** ACTIONS PART  */
export const add_action = (scene, action) => {
	let _scene: any = _deepClone(scene);
	if (_scene?.do?.length > 0) {
		_scene.do.push(action);
	} else {
		_scene.do = [action];
	}

	return _scene;
};

export const edit_action = (scene, action, actionId) => {
	let _scene: any = _deepClone(scene);
	// console.log('EXISTING ID ', actionId, _scene?.do, scene?.do);
	if (_scene?.do[actionId] !== undefined) {
		_scene.do[actionId] = action;
	}
	return _scene;
};

/**seperate delete action and update actions array into 2 functions */

export const delete_actions_from_thens = (_thens, _ind) => {
	let filtered = _thens.filter((el, ind) => ind !== _ind);

	return filtered;
};

export const update_scene_with_new_thens = (_scene, thens) => {
	const scene: any = _deepClone(_scene);

	scene.do = _deepCopyArr(thens);

	return scene;
};

// old handler
//_________ ON DELETE ITEM ______
export const delete_action_and_update_thens = (scene, global, act_ind) => {
	const _scene: any = _deepClone(scene);

	let filtered = global.filter((el, ind) => ind !== act_ind);

	global = [...filtered];

	_scene.do = _deepCopyArr(global);

	return _scene;
};

/** ---- END ACTONS PART ---- */
